import { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase.js';

/**
 * AuthContext — the source of truth for "who is signed in."
 *
 * Wraps the app at the top level (in main.jsx) so every screen can
 * read the current user via useAuth().
 *
 * The provider:
 *   1. Asks Supabase for the existing session on mount (handles page
 *      reload — the session is persisted to localStorage by Supabase
 *      itself, so the user stays signed in across visits).
 *   2. Subscribes to onAuthStateChange so sign-in / sign-out / token
 *      refresh all update the React state automatically.
 *
 * Exposes: { user, session, loading, signOut }
 *   - loading: true until the initial session check completes; the
 *     app shows a quiet loading state during this brief window so we
 *     don't flash the sign-in screen at users who are already auth'd.
 */

const AuthContext = createContext({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    // 1. Initial session check.
    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session ?? null);
      setLoading(false);
    });

    // 2. Subscribe to changes (sign-in, sign-out, token refresh).
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      // After the first event, we're definitely no longer loading.
      setLoading(false);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const value = {
    session,
    user: session?.user ?? null,
    loading,
    signOut: () => supabase.auth.signOut(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
